import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { client } from '../../../../api/NestClient';
import { RTK_CACHE_LIFETIME } from '../../../../config';
import { nestBaseQuery } from '../../../../utils/nestBaseQuery';
import type { AccountDeletionResponse, RequestAccountDeletionPayload } from '../types/accountDeletion.types';

type NestClientCallResult =
  | Awaited<ReturnType<typeof client.GET>>
  | Awaited<ReturnType<typeof client.POST>>
  | Awaited<ReturnType<typeof client.PATCH>>;

const accountDeletionNestBaseQuery: BaseQueryFn<
  NestClientCallResult | Promise<NestClientCallResult>,
  unknown,
  { message: string; status: number; originalResponse: unknown }
> = async (clientCall) => nestBaseQuery(await clientCall);

export const accountDeletionApi = createApi({
  reducerPath: 'accountDeletionApi',
  baseQuery: accountDeletionNestBaseQuery,
  tagTypes: ['AccountDeletion'],
  endpoints: (builder) => ({
    getAccountDeletion: builder.query<AccountDeletionResponse | null, void>({
      query: () => client.GET('/api/v1/users/account-deletion'),
      transformResponse: (response: AccountDeletionResponse | undefined) => response ?? null,
      providesTags: ['AccountDeletion'],
      keepUnusedDataFor: RTK_CACHE_LIFETIME,
    }),
    requestAccountDeletion: builder.mutation<AccountDeletionResponse, RequestAccountDeletionPayload>({
      query: (body) => client.POST('/api/v1/users/account-deletion', { body }),
      invalidatesTags: ['AccountDeletion'],
    }),
    cancelAccountDeletion: builder.mutation<AccountDeletionResponse, void>({
      query: () => client.PATCH('/api/v1/users/account-deletion/cancel'),
      invalidatesTags: ['AccountDeletion'],
    }),
  }),
});

export const {
  useGetAccountDeletionQuery,
  useRequestAccountDeletionMutation,
  useCancelAccountDeletionMutation,
} = accountDeletionApi;
